import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import shipmentClientService from '../../services/ShipmentClientService.js';
import { Users, Plus, IndianRupee } from "lucide-react";
import { fullCurrency } from '../../utils/formatCurrency.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import {
  listIdLinkClass,
  listIdLinkMobileClass,
  filterInputClass,
  filterSelectClass,
  emptyStateClass,
  mobileCardClass,
  paginationFooterClass,
  listPageWrapperClass,
  tableClass,
  tableHeadRowClass,
  tableHeadCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
} from '../common/uiClasses.js';


const ShipmentClientList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAgent = user?.role === 'Agent';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (companyFilter) filters.company_name = companyFilter;

      const response = await shipmentClientService.getAll(currentPage, perPage, filters);
      setClients((prev) => mergeLoadedPage(currentPage, response.data, prev));
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load shipment clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, companyFilter, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchClients,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, statusFilter, companyFilter],
    debounceMs: 0,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (clientId, clientName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${clientName}"? This action cannot be undone.`,
      'Delete Shipment Client'
    );

    if (confirmed) {
      try {
        await shipmentClientService.delete(clientId);
        Alert.success('Shipment client deleted successfully!');

        if (clients.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchClients();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete shipment client');
      }
    }
  };

  const handleDownloadInvoice = async (clientId, clientName) => {
    try {
      await shipmentClientService.downloadClientInvoice(clientId);
      Alert.success(`Invoice downloaded for ${clientName}`);
    } catch (err) {
      Alert.error('Failed to download invoice');
      console.error(err);
    }
  };

  const handleSendInvoice = async (clientId, clientName) => {
    const confirmed = await Alert.confirm(
      `Send invoice to ${clientName}'s email?`,
      'Send Invoice'
    );

    if (confirmed) {
      try {
        await shipmentClientService.sendClientInvoice(clientId);
        Alert.success(`Invoice sent successfully to ${clientName}`);
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to send invoice');
      }
    }
  };

  const toggleDropdown = (clientId) => {
    setActiveDropdown(activeDropdown === clientId ? null : clientId);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCompanyFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || statusFilter || companyFilter;

  const getExtraItems = (client) => {
    if (isAgent) return [];
    return [
    {
      label: 'Pay',
      icon: '💰',
      onClick: () => navigate(`/shipment-clients/${client.id}/batch-bill`),
    },
    {
      label: 'Download Invoice',
      icon: '📄',
      onClick: () => handleDownloadInvoice(client.id, client.full_name),
    },
    {
      label: 'Email Invoice',
      icon: '📧',
      onClick: () => handleSendInvoice(client.id, client.full_name),
    },
    ];
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '₹0.00';
    return fullCurrency(amount);
  };

  const goToBatchPayment = (client) => {
    navigate(`/shipment-clients/${client.id}/batch-bill`);
  };

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader
          title={t('pages.shipmentClient.title')}
          icon={Users}
          titleClassName="text-secondary"
          iconClassName="w-5 h-5 text-secondary shrink-0"
          className="mb-4 sm:mb-4"
        >
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/shipment-clients/add')}>
            {t('pages.shipmentClient.add')}
          </Button>
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Name, email, contact..."
              inputClassName={filterInputClass}
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={filterSelectClass}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.company')}</label>
            <input
              type="text"
              placeholder="Company name..."
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className={filterInputClass}
            />
          </div>
        </ListFilterPanel>

        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading clients..." />
          </div>
        ) : clients.length === 0 ? (
          <div className={emptyStateClass}>No shipment clients found</div>
        ) : (
          <>
            {/* Table - iPad landscape / desktop */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-xl shadow-sm w-full min-w-0 overflow-x-auto">
                <table className={`${tableClass} min-w-full divide-y divide-gray-200`}>
                  <thead>
                    <tr className={tableHeadRowClass}>
                      <th scope="col" className={tableHeadCellClass}>Full Name</th>
                      <th scope="col" className={tableHeadCellClass}>Contact</th>
                      <th scope="col" className={tableHeadCellClass}>{t('common.company')}</th>
                      <th scope="col" className={tableHeadCellClass}>GST</th>
                      <th scope="col" className={`${tableHeadCellClass} text-right`}>Shipments</th>
                      <th scope="col" className={`${tableHeadCellClass} text-right`}>Total</th>
                      <th scope="col" className={`${tableHeadCellClass} text-right`}>Paid</th>
                      <th scope="col" className={`${tableHeadCellClass} text-right`}>Due</th>
                      <th scope="col" className={tableHeadCellClass}>Status</th>
                      <th scope="col" className={tableHeadCellClass}>{t('common.actions')}</th>
                    </tr>
                  </thead>

                  <tbody className={`${tableBodyClass} bg-white`}>
                    {clients.map((client) => (
                      <tr key={client.id} className={tableRowClass}>
                        <td className={tableCellClass}>
                          <Button variant="link" onClick={() => navigate(`/shipment-clients/view/${client.id}`)} className={listIdLinkClass}>
                            {client.full_name}
                          </Button>
                          <p className="text-xs text-gray-500 truncate max-w-[180px]" title={client.email}>{client.email}</p>
                        </td>
                        <td className={`${tableCellClass} text-sm text-gray-600`}>{client.contact}</td>
                        <td className={`${tableCellClass} text-sm text-gray-600`}>{client.company_name}</td>
                        <td className={`${tableCellClass} text-sm text-gray-600`}>{client.gst_number || '—'}</td>
                        <td className={`${tableCellClass} text-sm text-gray-700 text-right tabular-nums`}>
                          {client.shipment_count ?? 0}
                        </td>
                        <td className={`${tableCellClass} text-sm font-semibold text-blue-700 text-right tabular-nums`}>
                          {formatAmount(client.total_amount)}
                        </td>
                        <td className={`${tableCellClass} text-sm font-semibold text-green-700 text-right tabular-nums`}>
                          {formatAmount(client.paid_amount)}
                        </td>
                        <td className={`${tableCellClass} text-sm font-bold text-red-600 text-right tabular-nums`}>
                          {formatAmount(client.due_amount)}
                        </td>
                        <td className={tableCellClass}>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${client.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {capitalizeStatus(client.status)}
                          </span>
                        </td>
                        <td className={`${tableCellClass} relative`}>
                          <div className="flex items-center gap-1.5">
                            {!isAgent && (
                            <Button
                              variant="success"
                              size="sm"
                              icon={IndianRupee}
                              title="Pay"
                              onClick={() => goToBatchPayment(client)}
                            >
                              Pay
                            </Button>
                            )}
                            <RowActionsMenu
                              isOpen={activeDropdown === client.id}
                              onToggle={() => toggleDropdown(client.id)}
                              onClose={() => setActiveDropdown(null)}
                              menuRef={dropdownRef}
                              onView={() => navigate(`/shipment-clients/view/${client.id}`)}
                              onEdit={() => navigate(`/shipment-clients/edit/${client.id}`)}
                              onDelete={() => handleDelete(client.id, client.full_name)}
                              extraItems={getExtraItems(client)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  loading={loading}
                  onNext={() => setCurrentPage((page) => page + 1)}
                />
              </div>
            </div>

            {/* Card View - Mobile / iPad portrait */}
            <div className="lg:hidden space-y-3">
              {clients.map((client) => (
                <div key={client.id} className={mobileCardClass}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <h3
                        className={`${listIdLinkMobileClass} truncate`}
                        onClick={() => navigate(`/shipment-clients/view/${client.id}`)}
                      >
                        {client.full_name}
                      </h3>
                      <p className="text-xs text-gray-500 truncate">
                        {[client.company_name, client.contact].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                        client.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {capitalizeStatus(client.status)}
                      </span>
                      {!isAgent && (
                      <Button
                        variant="success"
                        size="sm"
                        icon={IndianRupee}
                        title="Pay"
                        mobileIconOnly
                        onClick={() => goToBatchPayment(client)}
                      >
                        Pay
                      </Button>
                      )}
                      <RowActionsMenu
                        isOpen={activeDropdown === client.id}
                        onToggle={() => toggleDropdown(client.id)}
                        onClose={() => setActiveDropdown(null)}
                        menuRef={dropdownRef}
                        onView={() => navigate(`/shipment-clients/view/${client.id}`)}
                        onEdit={() => navigate(`/shipment-clients/edit/${client.id}`)}
                        onDelete={() => handleDelete(client.id, client.full_name)}
                        extraItems={getExtraItems(client)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="rounded-xl bg-blue-50 px-2.5 py-2 min-w-0">
                      <p className="text-[10px] font-medium text-blue-700/80">Total</p>
                      <p className="text-xs sm:text-sm font-semibold text-blue-700 tabular-nums truncate">{formatAmount(client.total_amount)}</p>
                    </div>
                    <div className="rounded-xl bg-green-50 px-2.5 py-2 min-w-0">
                      <p className="text-[10px] font-medium text-green-700/80">Paid</p>
                      <p className="text-xs sm:text-sm font-semibold text-green-700 tabular-nums truncate">{formatAmount(client.paid_amount)}</p>
                    </div>
                    <div className="rounded-xl bg-red-50 px-2.5 py-2 min-w-0">
                      <p className="text-[10px] font-medium text-red-700/80">Due</p>
                      <p className="text-xs sm:text-sm font-bold text-red-600 tabular-nums truncate">{formatAmount(client.due_amount)}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] sm:text-xs text-gray-600">
                    {client.email ? <span className="truncate max-w-[70%]">{client.email}</span> : null}
                    <span>GST {client.gst_number || '—'}</span>
                    <span>{client.shipment_count ?? 0} shipment{(client.shipment_count ?? 0) === 1 ? '' : 's'}</span>
                  </div>
                </div>
              ))}

              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={loading}
                onNext={() => setCurrentPage((page) => page + 1)}
              />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ShipmentClientList;
